/**
 * An object on a rough inclined plane.
 *
 * With the x axis along the slope and the y axis perpendicular to it, gravity splits into
 * W∥ = mg sin θ (pulling down the slope) and W⊥ = mg cos θ (pressing into it). With no
 * force perpendicular to the surface other than gravity and the push of the plane, the
 * normal force is N = mg cos θ. Friction then has a ceiling — μs N while the object is
 * at rest — and a fixed size, μk N, once it slides (OpenStax University Physics Vol. 1,
 * §6.2 and §6.1). The object slides when the pull along the slope beats the static
 * ceiling, which for a free ramp (no applied force) means tan θ > μs.
 *
 * The object starts from rest. A push or pull F parallel to the slope is optional and is
 * positive up the slope.
 */

export interface InclineInput {
  /** Kilograms, > 0. */
  mass: number;
  /** Radians, from 0 up to but not including π/2. */
  angle: number;
  /** Coefficient of static friction, ≥ 0. */
  staticMu: number;
  /** Coefficient of kinetic friction, ≥ 0 and not above the static one. */
  kineticMu: number;
  /** m/s², > 0. */
  gravity: number;
  /** Force parallel to the slope in newtons, positive up the slope. */
  applied: number;
  /** Distance along the slope in metres, ≥ 0; null when not given. */
  length: number | null;
}

export type InclineError =
  | "mass"
  | "angle"
  | "static-mu"
  | "kinetic-mu"
  | "kinetic-exceeds-static"
  | "gravity"
  | "length";

export type Motion = "rest" | "down" | "up";

export interface InclineResult {
  weight: number;
  parallel: number;
  perpendicular: number;
  normal: number;
  maxStatic: number;
  kineticFriction: number;
  /** Net pull along the slope before friction, positive up the slope. */
  pull: number;
  motion: Motion;
  /** Size of the friction force acting. */
  friction: number;
  /** Which way friction points along the slope. */
  frictionDirection: "up" | "down" | "none";
  /** Net force along the slope after friction, positive up the slope. */
  net: number;
  /** Acceleration along the slope, positive up the slope. */
  acceleration: number;
  /** arctan(μs): the slope angle at which a free object begins to slide. */
  criticalAngle: number;
  /** Smallest push up the slope that starts the object moving up. */
  pushToStart: number;
  /** Push up the slope that keeps it moving at constant speed. */
  pushToKeepMoving: number;
  /** Push up the slope needed to stop it sliding down (0 when it already holds). */
  pushToHold: number;
  /** From rest over `length`: time and final speed; null at rest or without a length. */
  travel: { time: number; speed: number } | null;
  /** Ideal mechanical advantage of the ramp, 1 / sin θ (null for a flat ramp). */
  mechanicalAdvantage: number | null;
  /** Height gained over `length` (null without a length). */
  rise: number | null;
}

const HALF_PI = Math.PI / 2;

function validate(input: InclineInput): InclineError | null {
  const { mass, angle, staticMu, kineticMu, gravity, length } = input;
  const checks: [boolean, InclineError][] = [
    [!(mass > 0), "mass"],
    [!(angle >= 0 && angle < HALF_PI - 1e-9), "angle"],
    [staticMu < 0, "static-mu"],
    [kineticMu < 0, "kinetic-mu"],
    [kineticMu > staticMu, "kinetic-exceeds-static"],
    [!(gravity > 0), "gravity"],
    [length !== null && length < 0, "length"],
  ];
  return checks.find(([failed]) => failed)?.[1] ?? null;
}

interface Slip {
  motion: Motion;
  friction: number;
  frictionDirection: InclineResult["frictionDirection"];
  net: number;
}

/** What friction does, given the pull along the slope and its two ceilings. */
function slip(
  pull: number,
  maxStatic: number,
  kinetic: number,
  weight: number
): Slip {
  // The pull must beat the ceiling by more than rounding noise to count as motion.
  const slips = Math.abs(pull) > maxStatic * (1 + 1e-12) + 1e-12 * weight;
  if (!slips) {
    let direction: Slip["frictionDirection"] = "none";
    if (pull !== 0) {
      direction = pull < 0 ? "up" : "down";
    }
    return {
      motion: "rest",
      friction: Math.abs(pull),
      frictionDirection: direction,
      net: 0,
    };
  }
  const up = pull > 0;
  return {
    motion: up ? "up" : "down",
    friction: kinetic,
    frictionDirection: up ? "down" : "up",
    net: pull - (up ? kinetic : -kinetic),
  };
}

export function solveIncline(
  input: InclineInput
): InclineResult | { error: InclineError } {
  const invalid = validate(input);
  if (invalid !== null) {
    return { error: invalid };
  }
  const { mass, angle, staticMu, kineticMu, gravity, applied, length } = input;
  const weight = mass * gravity;
  const sin = Math.sin(angle);
  const parallel = weight * sin;
  const normal = weight * Math.cos(angle);
  const maxStatic = staticMu * normal;
  const kineticFriction = kineticMu * normal;
  const pull = applied - parallel;
  const state = slip(pull, maxStatic, kineticFriction, weight);
  const acceleration = state.net / mass;
  const travel =
    length !== null && length > 0 && state.motion !== "rest"
      ? {
          time: Math.sqrt((2 * length) / Math.abs(acceleration)),
          speed: Math.sqrt(2 * Math.abs(acceleration) * length),
        }
      : null;
  return {
    weight,
    parallel,
    perpendicular: normal,
    normal,
    maxStatic,
    kineticFriction,
    pull,
    ...state,
    acceleration,
    criticalAngle: Math.atan(staticMu),
    pushToStart: parallel + maxStatic,
    pushToKeepMoving: parallel + kineticFriction,
    pushToHold: Math.max(0, parallel - maxStatic),
    travel,
    mechanicalAdvantage: sin > 0 ? 1 / sin : null,
    rise: length === null ? null : length * sin,
  };
}
